import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { AlertCircle } from "lucide-react";

import { AuthForm, AuthPanel, Button, Field, PasswordInput } from "@h2j-data-team/ui";

import { authClient } from "@/lib/auth-client";
import { PRODUCT_NAME } from "@repo/shared";

const makeResetPasswordSchema = (t: TFunction) =>
  z
    .object({
      password: z.string().min(8, t("resetPassword.passwordMinLength")),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("resetPassword.passwordMismatch"),
      path: ["confirmPassword"],
    });

type ResetPasswordInput = z.infer<ReturnType<typeof makeResetPasswordSchema>>;

export const Route = createFileRoute("/_auth/reset-password")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: typeof search.token === "string" ? search.token : undefined,
    };
  },
  head: () => ({ meta: [{ title: `Reset password — ${PRODUCT_NAME}` }] }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { token } = Route.useSearch();
  const resetPasswordSchema = useMemo(() => makeResetPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordInput) => {
    if (!token) {
      setError("root", { message: t("resetPassword.missingToken") });
      return;
    }

    const result = await authClient.resetPassword({
      token,
      newPassword: data.password,
    });

    if (result.error) {
      setError("root", { message: result.error.message ?? t("resetPassword.failed") });
      return;
    }

    navigate({
      to: "/login",
      search: {
        mode: undefined,
        token: undefined,
        error: undefined,
        redirect: undefined,
        email: undefined,
      },
    });
  };

  if (!token) {
    return (
      <AuthPanel
        tone="danger"
        icon={<AlertCircle />}
        title={t("resetPassword.invalid.heading")}
        description={t("resetPassword.invalid.description")}
        actions={
          <Button size="lg" asChild>
            <Link to="/forgot-password">{t("resetPassword.invalid.requestNew")}</Link>
          </Button>
        }
      />
    );
  }

  return (
    <AuthForm
      noValidate
      title={t("resetPassword.heading")}
      description={t("resetPassword.description")}
      error={errors.root?.message}
      busy={isSubmitting}
      submitLabel={t("resetPassword.submit")}
      submittingLabel={t("resetPassword.updating")}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <Link
          to="/login"
          search={{
            mode: undefined,
            token: undefined,
            error: undefined,
            redirect: undefined,
            email: undefined,
          }}
        >
          {t("backToSignIn")}
        </Link>
      }
    >
      <Field
        id="password"
        label={t("resetPassword.newPasswordLabel")}
        error={errors.password?.message}
      >
        <PasswordInput
          autoComplete="new-password"
          showLabel={t("showPassword")}
          hideLabel={t("hidePassword")}
          {...register("password")}
        />
      </Field>

      <Field
        id="confirmPassword"
        label={t("resetPassword.confirmNewPasswordLabel")}
        error={errors.confirmPassword?.message}
      >
        <PasswordInput
          autoComplete="new-password"
          showLabel={t("showPassword")}
          hideLabel={t("hidePassword")}
          {...register("confirmPassword")}
        />
      </Field>
    </AuthForm>
  );
}
